import { useCallback, useEffect, useState } from "react";
import type { Entity } from "../../../components/EntityPicker";
import type {
  AvailableEdition,
  SubscriptionWithFeatures,
} from "../../../api/subscriptions.service";
import { subscriptions as subscriptionsService } from "../../../api/subscriptions.service";
import { contacts as contactsService } from "../../../api/contacts.service";
import { offices as officesService } from "../../../api/offices.service";
import { editorialCategories } from "../../../api/editorial-categories.service";

type PackageFeatures = SubscriptionWithFeatures["package"]["features"];

const CUSTOM_METADATA_FIELD_LABELS: Record<string, string> = {
  contact_ids: "Contacts",
  office_contact_pairs: "Offices & Contacts",
  editorial_category_ids: "Placement Locations",
};

export function formatMetadataKey(key: string): string {
  return key
    .split("_")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function formatEditionLabel(edition: AvailableEdition): string {
  const editionAndStatus = edition.status
    ? `${edition.edition} (${edition.status})`
    : edition.edition;
  return edition.publication ? `${edition.publication} — ${editionAndStatus}` : editionAndStatus;
}

/**
 * Resolves the display label for a `metadataQuantities` field key, preferring the
 * package feature's `entity_id_label`, falling back to a curated label map, and
 * finally to a formatted version of the raw key (e.g. `office_ids` -> `Office Ids`).
 */
export function getMetadataQuantityLabel(
  key: string,
  features: PackageFeatures | undefined,
): string {
  const feature = features?.find(f => f.entity_id_field === key);
  if (feature?.entity_id_label) {
    return feature.entity_id_label;
  }
  return CUSTOM_METADATA_FIELD_LABELS[key] || formatMetadataKey(key);
}

export type SubscriptionMetadataEntities = {
  /** Options for every metadata field the current package declares via `metadataQuantities`. */
  entityOptions: Record<string, Entity[]>;
  entityErrors: Record<string, string>;
  isLoadingEntities: boolean;
  /** Resolves the section/field label for a `metadataQuantities` key. */
  fieldLabel: (key: string) => string;
  /** Resolves an entity id to its display label, or `undefined` if unmapped. */
  labelForField: (fieldName: string, id: number | string) => string | undefined;
};

async function buildContactOptions(companyId: number): Promise<Entity[]> {
  const response = await contactsService.getAll({ limit: 100, companyWpId: companyId });
  if (!Array.isArray(response.contacts)) return [];
  return response.contacts.map(contact => ({
    value: contact.contactId,
    label: contact.name,
    description: contact.email ?? undefined,
  }));
}

async function buildEditorialCategoryOptions(): Promise<Entity[]> {
  const categories = await editorialCategories.getAll();
  return categories.map(category => ({
    value: category.id,
    label: category.name,
    group: category.type ? formatMetadataKey(category.type) : undefined,
  }));
}

async function buildPublicationEditionOptions(subscriptionId: string): Promise<Entity[]> {
  const editions = await subscriptionsService.getAvailableEditions(subscriptionId);
  return editions.map(edition => ({
    value: edition.publicationId,
    label: formatEditionLabel(edition),
  }));
}

async function buildOfficeOptions(companyId: number): Promise<Entity[]> {
  const response = await officesService.getAll({ companyWpId: companyId });
  return response.offices.map(office => {
    const name = office.name || `Office ${office.officeId}`;
    const location = [office.city, office.countryName].filter(Boolean).join(", ");
    return {
      value: office.officeId,
      label: location ? `${name} (${location})` : name,
      description: `ID: ${office.officeId}`,
    };
  });
}

/**
 * Fetches the entity-picker options for every `metadataQuantities` field a subscription's
 * package declares, and exposes label resolvers for both the metadata-quantity keys
 * themselves and the individual entity ids stored in `subscription.metadata`.
 *
 * Shared between `OnboardingTab` (initial configuration) and the Metadata tab's
 * display/edit views on `SubscriptionDetail`, so both stay in lockstep.
 */
export function useSubscriptionMetadataEntities(
  subscription: SubscriptionWithFeatures | null,
): SubscriptionMetadataEntities {
  const [entityOptions, setEntityOptions] = useState<Record<string, Entity[]>>({});
  const [entityErrors, setEntityErrors] = useState<Record<string, string>>({});
  const [isLoadingEntities, setIsLoadingEntities] = useState(false);

  const metadataQuantities = subscription?.package?.metadataQuantities || {};
  const firmId = subscription?.firmId;
  const subscriptionId = subscription?.id;

  useEffect(() => {
    let cancelled = false;

    async function fetchEntities() {
      if (!subscription) {
        setEntityOptions({});
        setEntityErrors({});
        return;
      }

      setIsLoadingEntities(true);
      const options: Record<string, Entity[]> = {};
      const errors: Record<string, string> = {};
      const companyId = Number(firmId);

      if (metadataQuantities.contact_ids && !Number.isNaN(companyId)) {
        try {
          options.contact_ids = await buildContactOptions(companyId);
        } catch (_error) {
          errors.contact_ids = "Failed to load contacts";
        }
      }

      if (metadataQuantities.editorial_category_ids) {
        try {
          options.editorial_category_ids = await buildEditorialCategoryOptions();
        } catch (_error) {
          errors.editorial_category_ids = "Failed to load editorial categories";
        }
      }

      if (metadataQuantities.publication_edition_ids && subscriptionId) {
        try {
          options.publication_edition_ids = await buildPublicationEditionOptions(subscriptionId);
        } catch (_error) {
          errors.publication_edition_ids = "Failed to load available editions";
        }
      }

      if (metadataQuantities.office_ids && !Number.isNaN(companyId)) {
        try {
          options.office_ids = await buildOfficeOptions(companyId);
        } catch (_error) {
          errors.office_ids = "Failed to load offices";
        }
      }

      if (cancelled) return;
      setEntityOptions(options);
      setEntityErrors(errors);
      setIsLoadingEntities(false);
    }

    fetchEntities();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(metadataQuantities), firmId, subscriptionId]);

  const fieldLabel = useCallback(
    (key: string) => getMetadataQuantityLabel(key, subscription?.package?.features),
    [subscription?.package?.features],
  );

  const labelForField = useCallback(
    (fieldName: string, id: number | string) =>
      entityOptions[fieldName]?.find(entity => String(entity.value) === String(id))?.label,
    [entityOptions],
  );

  return { entityOptions, entityErrors, isLoadingEntities, fieldLabel, labelForField };
}
